import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthStore } from "../../state/auth";
import { useTranslation } from "../../i18n";
import type { LangCode } from "../../i18n";
import {
  Camera, CheckCircle2, ChevronDown, Clock, FolderOpen,
  QrCode, ScanLine, X, AlertCircle, Download,
} from "lucide-react";
import { BrowserQRCodeReader } from "@zxing/browser";

// ─── Types ───────────────────────────────────────────────────────────────────

interface DeployProject {
  id: string;
  hospital_name: string;
  hospital_address: string | null;
  status: string;
}

interface DeployCheckpoint {
  id: string;
  project_id: string;
  stop_number: number | null;
  floor: string | null;
  area: string | null;
  location_detail: string | null;
  placement_notes: string | null;
  qr_payload: string;
  status: string;
  capture_photo_url: string | null;
  installed_at: string | null;
  verified_at: string | null;
  installed_photo_url: string | null;
}

// ─── Auth Helpers ─────────────────────────────────────────────────────────────

function getAuthHeaders(): Record<string, string> {
  const { token, user } = useAuthStore.getState();
  const h: Record<string, string> = { "Content-Type": "application/json" };
  const csrf = localStorage.getItem("csrfToken");
  if (csrf) h["x-csrf-token"] = csrf;
  if (token) h["Authorization"] = `Bearer ${token}`;
  if (user?.email) h["x-user-email"] = user.email;
  return h;
}

async function apiGet(action: string, extra = "") {
  const r = await fetch(`/api/deployments?action=${action}${extra}`, {
    headers: getAuthHeaders(), credentials: "include",
  });
  if (!r.ok) throw new Error(`API ${r.status}`);
  return r.json();
}

async function apiPost(action: string, body: unknown) {
  const r = await fetch(`/api/deployments?action=${action}`, {
    method: "POST",
    headers: getAuthHeaders(),
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`API ${r.status}`);
  return r.json();
}

// ─── Project status label (backend enum → localized) ─────────────────────────

function projectStatusLabel(status: string, t: (key: string, fallback?: string) => string): string {
  switch (status) {
    case "planning": return t("deploy.statusPlanning", "planning");
    case "capturing": return t("deploy.statusCapturing", "capturing");
    case "fabricating": return t("deploy.statusFabricating", "fabricating");
    case "installing": return t("deploy.statusInstalling", "installing");
    case "complete": return t("deploy.statusComplete", "complete");
    default: return status;
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function VerifyWalkthrough() {
  const { t, lang } = useTranslation();
  const user = useAuthStore(s => s.user);

  const [projects, setProjects] = useState<DeployProject[]>([]);
  const [activeProject, setActiveProject] = useState<DeployProject | null>(null);
  const [checkpoints, setCheckpoints] = useState<DeployCheckpoint[]>([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);

  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<{ ok: boolean; payload: string } | null>(null);
  const [installedPhoto, setInstalledPhoto] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReport, setShowReport] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const qrReaderRef = useRef<BrowserQRCodeReader | null>(null);
  const controlsRef = useRef<any>(null);

  // ── Load projects ─────────────────────────────────────────────────────────
  useEffect(() => {
    loadProjects();
    return () => stopScanner();
  }, []);

  async function loadProjects() {
    try {
      const data = await apiGet("projects");
      setProjects(data.projects || []);
    } catch { /* ignore */ }
  }

  async function selectProject(id: string) {
    try {
      const data = await apiGet("project", `&id=${id}`);
      setActiveProject(data.project);
      setCheckpoints(data.checkpoints || []);
      setShowProjectDropdown(false);
    } catch (e: any) {
      setError(e.message);
    }
  }

  // ── QR Scanner ────────────────────────────────────────────────────────────
  async function startScanner() {
    setScanning(true);
    setScanResult(null);

    try {
      const reader = new BrowserQRCodeReader();
      qrReaderRef.current = reader;

      const devices = await BrowserQRCodeReader.listVideoInputDevices();
      const backCamera = devices.find(d => d.label.toLowerCase().includes("back") || d.label.toLowerCase().includes("rear")) || devices[0];
      if (!backCamera) throw new Error(t("deploy.noCameraFound", "No camera found"));

      controlsRef.current = await reader.decodeFromVideoDevice(
        backCamera.deviceId,
        videoRef.current!,
        (result, err) => {
          if (result) {
            handleScanResult(result.getText());
          }
        }
      );
    } catch (e: any) {
      setError(`${t("deploy.cameraError", "Camera error:")} ${e.message}`);
      setScanning(false);
    }
  }

  function stopScanner() {
    controlsRef.current?.stop();
    controlsRef.current = null;
    setScanning(false);
  }

  function handleScanResult(payload: string) {
    stopScanner();
    const ck = checkpoints.find(c => c.id === verifyingId);
    if (!ck) return;

    const expected = ck.qr_payload;
    const matched = payload === expected || payload.includes(expected);
    setScanResult({ ok: matched, payload });
  }

  // ── Photo capture ─────────────────────────────────────────────────────────
  function handleInstalledPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setInstalledPhoto(ev.target?.result as string);
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  // ── Open verify panel ─────────────────────────────────────────────────────
  function openVerify(ck: DeployCheckpoint) {
    stopScanner();
    setVerifyingId(ck.id);
    setScanResult(null);
    setInstalledPhoto(ck.installed_photo_url || null);
    setError(null);
  }

  // ── Mark as installed & verified ─────────────────────────────────────────
  async function markVerified() {
    if (!verifyingId) return;
    setSaving(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      await apiPost("update-checkpoint", {
        id: verifyingId,
        status: "verified",
        installed_by: user?.name || user?.email || "unknown",
        installed_at: now,
        verified_at: now,
        installed_photo_url: installedPhoto || null,
      });
      setCheckpoints(prev => prev.map(c =>
        c.id === verifyingId
          ? { ...c, status: "verified", verified_at: now, installed_at: now, installed_photo_url: installedPhoto }
          : c
      ));
      setVerifyingId(null);
      setScanResult(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Completion stats ──────────────────────────────────────────────────────
  const verifiedCount = checkpoints.filter(c => c.status === "verified" || c.status === "installed").length;
  const total = checkpoints.length;
  const allDone = total > 0 && verifiedCount === total;

  // ─── Floors (grouped) ─────────────────────────────────────────────────────
  const floors = [...new Set(checkpoints.map(c => c.floor || "?"))].sort();

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <QrCode className="w-5 h-5 text-blue-600" />
          <h1 className="text-base font-semibold text-gray-900">{t("deploy.installVerify", "Install & Verify")}</h1>
        </div>
        {allDone && (
          <button
            onClick={() => setShowReport(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700"
          >
            <Download className="w-3.5 h-3.5" /> {t("deploy.completionReport", "Completion Report")}
          </button>
        )}
      </div>

      {error && (
        <div className="mx-4 mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Project selector */}
      <div className="px-4 py-3 bg-white border-b border-gray-100">
        <div className="relative">
          <button
            onClick={() => setShowProjectDropdown(!showProjectDropdown)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded border border-gray-300 bg-white w-full"
          >
            <FolderOpen className="w-4 h-4 text-gray-400" />
            <span className="flex-1 text-left truncate text-gray-700">
              {activeProject ? activeProject.hospital_name : t("deploy.selectProjectShort", "Select project…")}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
          {showProjectDropdown && (
            <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
              {projects.map(p => (
                <button
                  key={p.id}
                  onClick={() => selectProject(p.id)}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 border-b border-gray-50 last:border-0"
                >
                  {p.hospital_name}
                  <span className="ml-2 text-xs text-gray-400">{projectStatusLabel(p.status, t)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {activeProject ? (
        <>
          {/* Progress */}
          <div className="px-4 py-3 bg-white border-b border-gray-100">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-gray-600 font-medium">
                {t("deploy.installedVerifiedProgress", "{done} / {total} installed & verified").replace("{done}", String(verifiedCount)).replace("{total}", String(total))}
              </span>
              {allDone && (
                <span className="text-xs font-semibold text-green-600">{t("deploy.complete", "Complete!")}</span>
              )}
            </div>
            <div className="h-2 bg-gray-200 rounded-sm">
              <div
                className="h-2 bg-green-500 rounded-sm transition-all"
                style={{ width: total > 0 ? `${(verifiedCount / total) * 100}%` : "0%" }}
              />
            </div>
          </div>

          {/* Verify panel */}
          {verifyingId && (
            <VerifyPanel
              checkpoint={checkpoints.find(c => c.id === verifyingId)!}
              t={t}
              scanning={scanning}
              scanResult={scanResult}
              installedPhoto={installedPhoto}
              saving={saving}
              videoRef={videoRef}
              onStartScan={startScanner}
              onStopScan={stopScanner}
              onPhoto={() => cameraInputRef.current?.click()}
              onMarkVerified={markVerified}
              onClose={() => { stopScanner(); setVerifyingId(null); }}
            />
          )}

          {/* Checkpoint list by floor */}
          {!verifyingId && (
            <div className="flex-1 overflow-y-auto">
              {floors.map(floor => (
                <div key={floor}>
                  <div className="sticky top-0 bg-gray-100 px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide z-10">
                    {t("deploy.floorN", "Floor {n}").replace("{n}", floor)}
                  </div>
                  {checkpoints
                    .filter(c => (c.floor || "?") === floor)
                    .sort((a, b) => (a.stop_number ?? 999) - (b.stop_number ?? 999))
                    .map(ck => (
                      <VerifyCheckpointRow
                        key={ck.id}
                        ck={ck}
                        t={t}
                        lang={lang}
                        onVerify={() => openVerify(ck)}
                      />
                    ))}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          {t("deploy.selectProjectToVerify", "Select a project to start verifying.")}
        </div>
      )}

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleInstalledPhoto}
      />

      {/* Completion report modal */}
      {showReport && activeProject && (
        <CompletionReport
          project={activeProject}
          checkpoints={checkpoints}
          t={t}
          lang={lang}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
}

// ─── Verify Panel ─────────────────────────────────────────────────────────────

function VerifyPanel({
  checkpoint, t, scanning, scanResult, installedPhoto, saving,
  videoRef, onStartScan, onStopScan, onPhoto, onMarkVerified, onClose,
}: {
  checkpoint: DeployCheckpoint;
  t: (key: string, fallback?: string) => string;
  scanning: boolean;
  scanResult: { ok: boolean; payload: string } | null;
  installedPhoto: string | null;
  saving: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
  onStartScan: () => void;
  onStopScan: () => void;
  onPhoto: () => void;
  onMarkVerified: () => void;
  onClose: () => void;
}) {
  return (
    <div className="bg-white border-b border-gray-200 px-4 py-4 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-900">
          {t("deploy.stopNumber", "Stop #{n}").replace("{n}", String(checkpoint.stop_number))} — {checkpoint.area}
        </p>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Placement reference photo */}
      {checkpoint.capture_photo_url && (
        <div>
          <p className="text-xs text-gray-500 mb-1">{t("deploy.placeCardHere", "Place the card HERE (from capture):")}</p>
          <img src={checkpoint.capture_photo_url} alt={t("deploy.placementReferenceAlt", "placement reference")} className="h-32 rounded object-cover border" />
        </div>
      )}

      <div className="bg-gray-50 rounded p-3 text-xs space-y-1">
        <p><span className="text-gray-500">{t("deploy.floorColon", "Floor:")}</span> {checkpoint.floor}</p>
        <p><span className="text-gray-500">{t("deploy.areaLabel", "Area:")}</span> {checkpoint.area}</p>
        {checkpoint.location_detail && <p><span className="text-gray-500">{t("deploy.locationLabel", "Location:")}</span> {checkpoint.location_detail}</p>}
        {checkpoint.placement_notes && <p><span className="text-gray-500">{t("deploy.notesLabel", "Notes:")}</span> {checkpoint.placement_notes}</p>}
        <p className="font-mono text-gray-400 break-all">{t("deploy.uuidPrefix", "UUID:")} {checkpoint.qr_payload}</p>
      </div>

      {/* QR Scanner */}
      <div>
        <p className="text-xs font-medium text-gray-700 mb-2">{t("deploy.scanToVerify", "Scan to Verify")}</p>
        {scanning ? (
          <div className="space-y-2">
            <video ref={videoRef} className="w-full h-48 rounded bg-black object-cover" />
            <button onClick={onStopScan} className="w-full py-2 text-xs border border-gray-300 rounded hover:bg-gray-50">
              {t("deploy.cancelScan", "Cancel Scan")}
            </button>
          </div>
        ) : scanResult ? (
          <div className={`flex items-center gap-2 p-3 rounded text-sm ${scanResult.ok ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`}>
            {scanResult.ok
              ? <><CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /> {t("deploy.uuidMatched", "UUID matched! QR verified.")}</>
              : <><AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" /> {t("deploy.uuidMismatch", "UUID mismatch. Wrong QR card.")}<br />{t("deploy.scannedPrefix", "Scanned:")} {scanResult.payload.slice(0, 40)}…</>
            }
          </div>
        ) : (
          <button
            onClick={onStartScan}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700"
          >
            <ScanLine className="w-4 h-4" /> {t("deploy.scanQrCode", "Scan QR Code")}
          </button>
        )}
        {scanResult && (
          <button onClick={onStartScan} className="mt-1 w-full py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50">
            {t("deploy.scanAgain", "Scan Again")}
          </button>
        )}
      </div>

      {/* Installed photo */}
      <div>
        <button
          onClick={onPhoto}
          className="flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          <Camera className="w-3.5 h-3.5" />
          {installedPhoto ? t("deploy.retakeInstalledPhoto", "Retake Installed Photo") : t("deploy.takeInstalledPhoto", "Take Installed Photo")}
        </button>
        {installedPhoto && (
          <img src={installedPhoto} alt={t("deploy.installedAlt", "installed")} className="mt-2 h-28 rounded object-cover border" />
        )}
      </div>

      {/* Mark verified */}
      <button
        onClick={onMarkVerified}
        disabled={saving}
        className={`w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded ${
          scanResult?.ok
            ? "bg-green-600 text-white hover:bg-green-700"
            : "bg-gray-600 text-white hover:bg-gray-700"
        } disabled:opacity-50`}
      >
        <CheckCircle2 className="w-4 h-4" />
        {saving ? t("common.saving", "Saving…") : t("deploy.markInstalledVerified", "Mark Installed & Verified")}
      </button>
    </div>
  );
}

// ─── Checkpoint Row ───────────────────────────────────────────────────────────

function VerifyCheckpointRow({ ck, t, lang, onVerify }: { ck: DeployCheckpoint; t: (key: string, fallback?: string) => string; lang: LangCode; onVerify: () => void }) {
  const verified = ck.status === "verified" || ck.status === "installed";
  return (
    <div className={`flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-white ${verified ? "opacity-70" : ""}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${verified ? "bg-green-100" : "bg-gray-100 text-gray-600"}`}>
        {verified ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : (ck.stop_number ?? "?")}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{ck.area || t("deploy.unnamed", "Unnamed")}</p>
        <p className="text-xs text-gray-500">{ck.location_detail || ""}</p>
        {verified && ck.verified_at && (
          <p className="text-xs text-green-600 flex items-center gap-1 mt-0.5">
            <Clock className="w-3 h-3" />
            {new Intl.DateTimeFormat(lang, { hour: "numeric", minute: "2-digit", second: "2-digit" }).format(new Date(ck.verified_at))}
          </p>
        )}
      </div>
      {ck.capture_photo_url && (
        <img src={ck.capture_photo_url} alt="" className="w-10 h-10 rounded object-cover border border-gray-200 flex-shrink-0" />
      )}
      <button
        onClick={onVerify}
        className={`px-3 py-1.5 text-xs rounded font-medium flex-shrink-0 ${
          verified
            ? "border border-gray-300 text-gray-500 hover:bg-gray-50"
            : "bg-blue-600 text-white hover:bg-blue-700"
        }`}
      >
        {verified ? t("deploy.reverify", "Re-verify") : t("deploy.verify", "Verify")}
      </button>
    </div>
  );
}

// ─── Completion Report Modal ──────────────────────────────────────────────────

function CompletionReport({
  project, checkpoints, t, lang, onClose,
}: {
  project: DeployProject;
  checkpoints: DeployCheckpoint[];
  t: (key: string, fallback?: string) => string;
  lang: LangCode;
  onClose: () => void;
}) {
  const verified = checkpoints.filter(c => c.status === "verified" || c.status === "installed");
  const notVerified = checkpoints.filter(c => c.status !== "verified" && c.status !== "installed");

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-900">{t("deploy.completionReport", "Completion Report")}</p>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="text-xs px-2 py-1 border rounded hover:bg-gray-50">{t("deploy.print", "Print")}</button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="px-4 py-4 space-y-3">
          <div>
            <p className="font-semibold text-gray-900">{project.hospital_name}</p>
            {project.hospital_address && <p className="text-xs text-gray-500">{project.hospital_address}</p>}
            <p className="text-xs text-gray-400 mt-1">{t("deploy.generated", "Generated:")} {new Intl.DateTimeFormat(lang, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date())}</p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-gray-50 rounded p-3">
              <p className="text-2xl font-bold text-gray-800">{checkpoints.length}</p>
              <p className="text-xs text-gray-500">{t("deploy.total", "Total")}</p>
            </div>
            <div className="bg-green-50 rounded p-3">
              <p className="text-2xl font-bold text-green-700">{verified.length}</p>
              <p className="text-xs text-green-600">{t("deploy.verifiedCount", "Verified")}</p>
            </div>
            <div className="bg-red-50 rounded p-3">
              <p className="text-2xl font-bold text-red-700">{notVerified.length}</p>
              <p className="text-xs text-red-500">{t("status.pending", "Pending")}</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-2">{t("deploy.verifiedCheckpoints", "Verified Checkpoints")}</p>
            {verified.map(ck => (
              <div key={ck.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50 text-sm">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span className="flex-1 text-gray-700">
                  #{ck.stop_number} {ck.area} <span className="text-gray-400">— {t("deploy.floorN", "Floor {n}").replace("{n}", ck.floor ?? "?")}</span>
                </span>
                {ck.verified_at && (
                  <span className="text-xs text-gray-400">{new Intl.DateTimeFormat(lang, { hour: "numeric", minute: "2-digit", second: "2-digit" }).format(new Date(ck.verified_at))}</span>
                )}
              </div>
            ))}
          </div>
          {notVerified.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-red-600 mb-2">{t("deploy.notYetVerified", "Not Yet Verified")}</p>
              {notVerified.map(ck => (
                <div key={ck.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50 text-sm">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <span className="text-gray-700">#{ck.stop_number} {ck.area} — {t("deploy.floorN", "Floor {n}").replace("{n}", ck.floor ?? "?")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
